import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import { ILogger, ITopicEnvelope } from '@sparkware/uc-sdk-core';
import { WindowToken } from '../../injection-tokens';
import { DeviceUtils } from '../utils/deviceUtils';
import { PokerNativeInterfaceElasticEvents } from '../../models/enums/general-enums';
import { ClientTracking } from '../tracking/models/clientTracking';
import { IPokerNativeInterface } from './IPokerNativeInterface';

@Service()
export class PokerNative implements IPokerNativeInterface {
    private readonly _window: Window;
    private readonly _logger: ILogger;
    private readonly _deviceUtils: DeviceUtils;
    private readonly _clientTracking: ClientTracking;
    private readonly _clientTrackingEnvelop: ITopicEnvelope;

    /**
     * returns the poker native interface for both:
     * 1. the poker app which is poker in sport
     * 2. the bridge interface
     * @private
     */
    private get pokerNativeInterface(): IPokerNativeInterface {
        return this._window.PokerNativeInterface || this._window.external;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('PokerNativeService');
        this._deviceUtils = Container.get(DeviceUtils);
        this._clientTracking = Container.get(ClientTracking);
        this._clientTrackingEnvelop = {
            headers: {
                publisher: 'PokerNativeService',
            },
            topic: null,
        };
    }

    public onSiteLoaded = async (): Promise<void> => {
        if (!this._deviceUtils.isPokerApp()) return;
        this._clientTracking.onSendEvent(
            {
                event: PokerNativeInterfaceElasticEvents.OnSiteLoaded,
            },
            this._clientTrackingEnvelop,
        );
        await this.pokerNativeInterface.onSiteLoaded();
    };

    public onAutologinSuccess = async (): Promise<void> => {
        if (!this._deviceUtils.isPokerApp()) return;
        this._clientTracking.onSendEvent(
            {
                event: PokerNativeInterfaceElasticEvents.OnAutologinSuccess,
            },
            this._clientTrackingEnvelop,
        );
        await this.pokerNativeInterface.onAutologinSuccess();
    };

    public onAutologinFailed = async (): Promise<void> => {
        if (!this._deviceUtils.isPokerApp()) return;
        this._clientTracking.onSendEvent(
            {
                event: PokerNativeInterfaceElasticEvents.OnAutologinFailed,
            },
            this._clientTrackingEnvelop,
        );
        await this.pokerNativeInterface.onAutologinFailed();
    };

    public performUCAction = async (payload: any): Promise<void> => {
        if (!this._deviceUtils.isPokerApp()) return;
        this._clientTracking.onSendEvent(
            {
                event: PokerNativeInterfaceElasticEvents.OnPerformAction,
                dataString: JSON.stringify(payload),
            },
            this._clientTrackingEnvelop,
        );
        await this.pokerNativeInterface.performUCAction(JSON.stringify(payload));
    };

    public sendMessage = async (payload: any): Promise<void> => {
        if (!this._deviceUtils.isPokerApp()) return;
        await this.pokerNativeInterface.sendMessage(payload);
    };
}
